import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import RichTextEditor from './RichTextEditor';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Grimoire = ({ user }: any) => {
  const [entries, setEntries] = useState([]);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);

  const fetchGrimoire = useCallback(async () => {
    const res = await axios.get(`${API_URL}/grimoire`, {
      headers: { Authorization: `Bearer ${user.token}` }
    });
    setEntries(res.data);
  }, [user.token]);

  useEffect(() => {
    fetchGrimoire();
  }, [fetchGrimoire]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      await axios.put(`${API_URL}/grimoire/${editingId}`, 
        { title, category, content, is_private: isPrivate ? 1 : 0 },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setEditingId(null);
    } else {
      await axios.post(`${API_URL}/grimoire`, 
        { title, category, content, is_private: isPrivate ? 1 : 0 },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
    }
    setTitle('');
    setCategory('');
    setContent('');
    fetchGrimoire();
  };

  const startEdit = (entry: any) => {
    setEditingId(entry.id);
    setTitle(entry.title);
    setCategory(entry.category || '');
    setContent(entry.content);
    setIsPrivate(entry.is_private === 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const togglePrivacy = async (id: number, currentPrivacy: number) => {
    await axios.put(`${API_URL}/grimoire/${id}/privacy`, 
      { is_private: currentPrivacy === 1 ? 0 : 1 },
      { headers: { Authorization: `Bearer ${user.token}` } }
    );
    fetchGrimoire();
  };

  const deleteEntry = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this grimoire entry?')) {
      try {
        await axios.delete(`${API_URL}/grimoire/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchGrimoire();
      } catch (err) {
        console.error('Failed to delete grimoire entry', err);
        alert('Failed to delete entry.');
      }
    }
  };

  return (
    <div className="content">
      <h2>Your Digital Grimoire</h2>
      <div className="card">
        <h3>{editingId ? 'Edit Grimoire Entry' : 'New Grimoire Entry'}</h3>
        <form onSubmit={handleSubmit}>
          <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} required />
          <input placeholder="Category (e.g. Ritual, Recipe, Correspondence)" value={category} onChange={e => setCategory(e.target.value)} />
          <RichTextEditor content={content} onChange={setContent} placeholder="The sacred knowledge..." />
          <div style={{ marginBottom: '1rem' }}>
            <label>
              <input type="checkbox" checked={isPrivate} onChange={e => setIsPrivate(e.target.checked)} style={{ width: 'auto', marginRight: '0.5rem' }} />
              Keep Private
            </label>
          </div>
          <button type="submit">{editingId ? 'Update Entry' : 'Add to Grimoire'}</button>
          {editingId && (
            <button 
              type="button" 
              onClick={() => { setEditingId(null); setTitle(''); setCategory(''); setContent(''); }}
              style={{ marginLeft: '1rem', backgroundColor: '#666' }}
            >
              Cancel
            </button>
          )}
        </form>
      </div>
      
      <div className="grid">
        {entries.map((entry: any) => (
          <div key={entry.id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span className="badge">{entry.category || 'General'}</span>
                <span className="badge" style={{ backgroundColor: entry.is_private ? 'var(--accent-purple)' : '#2e7d32' }}>
                  {entry.is_private ? 'Private' : 'Public'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => startEdit(entry)} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>
                  Edit
                </button>
                <button onClick={() => togglePrivacy(entry.id, entry.is_private)} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>
                  {entry.is_private ? 'Publish' : 'Keep Private'}
                </button>
                <button onClick={() => deleteEntry(entry.id)} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', backgroundColor: '#8b0000' }}>
                  Delete
                </button>
              </div>
            </div>
            <h3>{entry.title}</h3>
            <div className="rich-content" dangerouslySetInnerHTML={{ __html: entry.content }} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Grimoire;
